import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3, Brain, MessageCircle, Users, Clock, Database, 
  TrendingUp, Layers, Settings, ChevronDown, 
  Bell, User, Menu, X, LogOut
} from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { User as UserType, UserRole } from '@/types';
import { navigationModules } from '@/data/mockData';

interface TopNavBarProps {
  user: UserType;
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const iconMap: Record<string, any> = {
  BarChart3, Brain, MessageCircle, Users, Clock, Database,
  TrendingUp, Layers, Settings
};

const TopNavBar: React.FC<TopNavBarProps> = ({ user, currentRole, onRoleChange }) => {
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const roleRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const { instance } = useMsal();

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const handleModuleClick = (module: typeof navigationModules[0]) => {
    navigate(module.path);
    setMobileMenuOpen(false);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleRef.current && !roleRef.current.contains(event.target as Node)) {
        setRoleDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setRoleDropdownOpen(false);
        setMobileMenuOpen(false);
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const isActiveModule = (path: string): boolean => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      <nav 
        id="primary-navigation"
        className="fixed top-0 left-0 right-0 z-50 h-[70px] bg-[#0A1628] border-b border-navy-border/60"
        style={{ backdropFilter: 'blur(12px)' }}
        role="navigation" 
        aria-label="Primary navigation"
      >
        <div className="h-full flex items-center justify-between px-4 lg:px-6">
          
          {/* LEFT — Logo */}
          <div className="flex items-center space-x-3 min-w-[140px]">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden p-2 text-text-muted hover:text-text-heading transition-colors rounded-md"
              aria-label="Toggle navigation"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gold-primary to-gold-pressed flex items-center justify-center">
                <span className="text-navy-primary font-black text-xs">N</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-gold-primary font-bold text-base tracking-wider">AFN</span>
                <span className="text-text-heading font-bold text-base tracking-wider ml-1">NEXUS</span>
              </div>
            </div>
          </div>

          {/* CENTER — Module Tabs */}
          <div className="hidden xl:flex items-center space-x-1 flex-1 justify-center max-w-4xl">
            {navigationModules.map((module) => {
              const IconComponent = iconMap[module.icon];
              const isActive = isActiveModule(module.path);
              
              return (
                <button
                  key={module.id}
                  className={`
                    flex items-center space-x-1.5 px-3 py-2 rounded-md text-[13px] font-medium
                    transition-all duration-200 ease-out relative whitespace-nowrap
                    ${isActive 
                      ? 'text-gold-primary' 
                      : 'text-text-muted hover:text-text-heading hover:bg-white/[0.04]'
                    }
                  `}
                  onClick={() => handleModuleClick(module)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {IconComponent && <IconComponent size={15} />}
                  <span>{module.label}</span>
                  {isActive && (
                    <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gold-primary rounded-full animate-slide-in" />
                  )}
                </button>
              );
            })}
          </div>

          {/* RIGHT — Role selector + notifications + profile */}
          <div className="flex items-center space-x-3 min-w-[140px] justify-end">
            {/* Notifications */}
            <button 
              className="relative p-2 text-text-muted hover:text-text-heading transition-colors rounded-md"
              aria-label="Notifications"
            >
              <Bell size={17} />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" aria-hidden="true" />
            </button>

            {/* Role Selector */}
            <div className="relative hidden md:block" ref={roleRef}>
              <button
                onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                className={`
                  flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                  border transition-all duration-200
                  ${roleDropdownOpen 
                    ? 'border-gold-primary/50 bg-gold-primary/5 text-gold-primary'
                    : 'border-navy-border text-text-muted hover:text-text-heading hover:border-navy-border/80'
                  }
                `}
                aria-expanded={roleDropdownOpen}
                aria-haspopup="true"
              >
                <span>{currentRole}</span>
                <ChevronDown size={13} className={`transition-transform duration-200 ${roleDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {roleDropdownOpen && (
                <div className="absolute right-0 mt-1.5 w-44 bg-navy-card border border-navy-border rounded-lg shadow-xl py-1 z-50 overflow-hidden">
                  {user.roles.map((role) => (
                    <button
                      key={role}
                      onClick={() => {
                        onRoleChange(role);
                        setRoleDropdownOpen(false);
                      }}
                      className={`
                        w-full px-3.5 py-2 text-left text-xs transition-all duration-150
                        ${role === currentRole 
                          ? 'bg-gold-primary/10 text-gold-primary font-medium' 
                          : 'text-text-primary hover:bg-white/[0.04] hover:text-text-heading'
                        }
                      `}
                    >
                      {role}
                      {role === currentRole && <span className="float-right text-gold-primary">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center space-x-2.5 p-1 rounded-md hover:bg-white/[0.04] transition-colors"
              >
                <div className="text-right hidden lg:block">
                  <p className="text-xs font-medium text-text-heading leading-tight">{user.name}</p>
                  <p className="text-[10px] text-text-muted leading-tight">{user.email}</p>
                </div>
                
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full border-2 border-navy-border hover:border-gold-primary/50 transition-colors"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gold-primary to-gold-pressed flex items-center justify-center">
                    <span className="text-navy-primary font-bold text-xs">
                      {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                )}
                <ChevronDown size={13} className={`text-text-muted transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileDropdownOpen && (
                <div className="absolute right-0 mt-1.5 w-52 bg-navy-card border border-navy-border rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-navy-border">
                    <p className="text-xs font-medium text-text-heading">{user.name}</p>
                    <p className="text-[10px] text-text-muted">{user.email}</p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-4 py-2.5 text-xs text-text-primary hover:bg-white/[0.04] hover:text-text-heading transition-colors"
                    >
                      <LogOut size={14} />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 xl:hidden">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          <div 
            ref={mobileMenuRef}
            className="fixed top-[70px] left-0 bottom-0 w-72 bg-navy-card border-r border-navy-border overflow-y-auto"
          >
            <div className="p-5">
              <nav className="space-y-1" aria-label="Mobile navigation">
                {navigationModules.map((module) => {
                  const IconComponent = iconMap[module.icon];
                  const isActive = isActiveModule(module.path);
                  
                  return (
                    <button
                      key={module.id}
                      className={`
                        w-full flex items-center space-x-3 px-3.5 py-2.5 rounded-lg text-left text-sm
                        transition-all duration-200
                        ${isActive 
                          ? 'bg-gold-primary/10 text-gold-primary font-semibold border-l-3 border-gold-primary' 
                          : 'text-text-primary hover:bg-white/[0.04] hover:text-text-heading'
                        }
                      `}
                      onClick={() => handleModuleClick(module)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {IconComponent && <IconComponent size={18} />}
                      <span>{module.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Mobile role selector */}
              <div className="mt-6 pt-5 border-t border-navy-border md:hidden">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2 px-1">Role</p>
                <div className="space-y-1">
                  {user.roles.map((role) => (
                    <button
                      key={role}
                      onClick={() => {
                        onRoleChange(role);
                        setMobileMenuOpen(false);
                      }}
                      className={`
                        w-full px-3.5 py-2 rounded-lg text-left text-sm transition-colors
                        ${role === currentRole 
                          ? 'bg-gold-primary/10 text-gold-primary font-medium' 
                          : 'text-text-primary hover:bg-white/[0.04]'
                        }
                      `}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopNavBar;
